import React, { useMemo, useState } from "react";
import { Box, Stack, Typography, Avatar } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { CompetitionVoteActions } from "./CompetitionBuyVotesTrigger";
import CompetitionVideoModal from "./CompetitionVideoModal";
import ContestantProfileModal from "./ContestantProfileModal";
import { contestantDisplayName } from "../../../utils/contestantDisplayName";
import { rankCompetitionVideos, MAX_VIDEO_RANK_DISPLAY } from "../../../utils/competitionVideoRank";
import { isCompetitionImageMedia } from "../../../utils/competitionEventMedia";
import CompetitionMediaThumbnail from "./CompetitionMediaThumbnail";
import {
  LIST_CARD_BG,
  LIST_MEDIA_BG,
  SITE_BORDER,
} from "../../../utils/siteListTheme";
import { MISS_GENERATION_DETAIL_SURFACE } from "../../../utils/missGenerationCompetition";
export {
  getVideoEventId,
  filterCompetitionVideos,
} from "../../../utils/competitionVideoRank";

function displayName(video) {
  return contestantDisplayName(video);
}

function VideoCard({
  video,
  votingEnabled,
  onVoted,
  onOpen,
  onProfileOpen,
  onCategoryClick,
  votePurchase,
  onBuyVotes,
  remainingVotes,
  maxFreeVotesPerDay,
  dailyVotesUsed,
  lightSurface = false,
  competitionBanner = "",
}) {
  const name = displayName(video);
  const avatar =
    video.contestant?.profile_image || video.contestant?.user?.profile_img;
  const category = video.category || video.contestant?.category || "—";
  const voteCount = video.voteCount ?? video.vote_count ?? 0;
  const rank = video.rank ?? 0;
  const rankLabel =
    rank >= 1 && rank <= MAX_VIDEO_RANK_DISPLAY ? video.rankLabel || "" : "";
  const isTop3 = rank >= 1 && rank <= 3;
  const isFirst = rank === 1;
  const isImage = isCompetitionImageMedia(video);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        ...(lightSurface
          ? {
              bgcolor: MISS_GENERATION_DETAIL_SURFACE,
              border: "1px solid #dddddd",
              boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
            }
          : {
              bgcolor: LIST_CARD_BG,
              border: `1px solid ${SITE_BORDER}`,
            }),
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
        transition:
          "border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: "linear-gradient(180deg, #ef364e, #F6E7C1)",
          opacity: 0,
          transition: "opacity 0.25s ease",
        },
        "&:hover": {
          borderColor: "rgba(239,54,78,0.45)",
          transform: "translateY(-2px)",
          boxShadow: lightSurface
            ? "0 12px 28px rgba(239,54,78,0.12), 0 8px 24px rgba(0,0,0,0.08)"
            : "0 16px 40px rgba(0,0,0,0.35)",
          "&::before": { opacity: 1 },
        },
      }}
    >
      <Box
        onClick={() => onOpen(video)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onOpen(video);
        }}
        sx={{
          position: "relative",
          width: { xs: "100%", sm: 280 },
          minWidth: { sm: 280 },
          height: { xs: 220, sm: 188 },
          flexShrink: 0,
          bgcolor: LIST_MEDIA_BG,
          overflow: "hidden",
          cursor: "pointer",
          "&:hover .play-veil": { background: "rgba(0,0,0,0.45)" },
          "&:hover .play-icon": { transform: "scale(1.1)" },
        }}
      >
        <CompetitionMediaThumbnail
          item={video}
          competitionBanner={competitionBanner}
          sx={{ transition: "transform 0.35s ease" }}
        />
        <Box
          className="play-veil"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            justifyContent: "flex-start",
            gap: 0.85,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 42%, rgba(0,0,0,0.78) 100%)",
            color: "#fff",
            transition: "background 0.25s ease",
            p: 1,
          }}
        >
          {isImage ? (
            <Box
              className="play-icon"
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(201,166,107,0.92)",
                boxShadow: "0 4px 14px rgba(201,166,107,0.35)",
                transition: "transform 0.25s ease",
              }}
            >
              <ImageOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
          ) : (
            <Box
              className="play-icon"
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(239,54,78,0.9)",
                boxShadow: "0 4px 14px rgba(239,54,78,0.45)",
                transition: "transform 0.25s ease",
              }}
            >
              <PlayCircleOutlineIcon sx={{ fontSize: 20 }} />
            </Box>
          )}
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontFamily: "Montserrat, sans-serif",
              fontSize: { xs: 12, sm: 13 },
              lineHeight: 1.2,
              textShadow: "0 2px 10px rgba(0,0,0,0.9)",
              pointerEvents: "none",
              pb: 0.25,
              minWidth: 0,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {video.event?.name || "Event"}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: { xs: 1.8, sm: 2.2 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.8,
          minWidth: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1.5}
        >
          <Box
            onClick={() => onOpen(video)}
            sx={{ cursor: "pointer", minWidth: 0, flex: 1 }}
          >
            <Typography
              sx={{
                display: { xs: "block", sm: "none" },
                color: lightSurface ? "#222222" : "#fff",
                fontWeight: 800,
                fontFamily: "Montserrat, sans-serif",
                fontSize: { xs: 16, sm: 18 },
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
              }}
            >
              {video.event?.name || "Event"}
            </Typography>
            <Typography
              onClick={(e) => {
                e.stopPropagation();
                if (category && category !== "—" && onCategoryClick) {
                  onCategoryClick(category);
                }
              }}
              sx={{
                color: lightSurface ? "#807154" : "#F6E7C1",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                mt: 0.5,
                opacity: lightSurface ? 1 : 0.85,
                cursor:
                  category && category !== "—" && onCategoryClick
                    ? "pointer"
                    : "default",
                "&:hover":
                  category && category !== "—" && onCategoryClick
                    ? { textDecoration: "underline" }
                    : undefined,
              }}
            >
              {category}
            </Typography>
          </Box>
          <Stack direction="column" alignItems="flex-end" flexShrink={0} gap={0.75}>
            {rankLabel ? (
              <Box
                aria-label={`${rankLabel} place`}
                sx={{
                  px: 1.25,
                  py: 0.35,
                  minWidth: 52,
                  textAlign: "center",
                  borderRadius: 999,
                  fontWeight: 800,
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: isFirst ? 13 : 12,
                  letterSpacing: "0.02em",
                  color: isFirst
                    ? "#1a1408"
                    : isTop3
                      ? "#2a2018"
                      : lightSurface
                        ? "#444444"
                        : "#fff",
                  bgcolor: isFirst
                    ? "#F0C75E"
                    : isTop3
                      ? "#E8C4A8"
                      : lightSurface
                        ? "#f0f0f0"
                        : "rgba(255,255,255,0.1)",
                  border: isTop3
                    ? "1px solid rgba(240,199,94,0.55)"
                    : lightSurface
                      ? "1px solid #dddddd"
                      : "1px solid rgba(255,255,255,0.18)",
                  boxShadow: isFirst
                    ? "0 4px 14px rgba(240,199,94,0.3)"
                    : "none",
                }}
              >
                {rankLabel}
              </Box>
            ) : null}
            <Box
              key={`${video.id}-${rankLabel}`}
              sx={{
                ...(rankLabel
                  ? {
                      animation: "competitionVoteSlideDown 0.32s ease forwards",
                      "@keyframes competitionVoteSlideDown": {
                        from: {
                          transform: "translateY(-10px)",
                          opacity: 0.65,
                        },
                        to: {
                          transform: "translateY(0)",
                          opacity: 1,
                        },
                      },
                    }
                  : {
                      transition: "transform 0.3s ease, opacity 0.3s ease",
                      transform: "translateY(-10px)",
                      opacity: 0.95,
                    }),
              }}
            >
              <CompetitionVoteActions
                videoId={video.id}
                eventId={video.event?.id || video.event_id}
                voteCount={voteCount}
                votingEnabled={votingEnabled}
                onVoted={onVoted}
                votePurchase={votePurchase}
                onBuyVotes={onBuyVotes}
                remainingVotes={remainingVotes}
                maxFreeVotesPerDay={maxFreeVotesPerDay}
                dailyVotesUsed={dailyVotesUsed}
                lightSurface={lightSurface}
              />
            </Box>
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1.1} mt={0.4}>
          <Avatar
            src={avatar}
            onClick={(e) => {
              e.stopPropagation();
              if (avatar)
                onProfileOpen?.({
                  image: avatar,
                  name,
                  contestant: video.contestant,
                });
            }}
            sx={{
              width: 32,
              height: 32,
              border: lightSurface
                ? "1.5px solid #dddddd"
                : "1.5px solid rgba(246,231,193,0.35)",
              cursor: avatar ? "pointer" : "default",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              ...(avatar
                ? {
                    "&:hover": {
                      transform: "scale(1.06)",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                    },
                  }
                : {}),
            }}
          >
            {name[0]}
          </Avatar>
          <Box minWidth={0}>
            <Typography
              sx={{
                color: lightSurface ? "#666666" : "rgba(255,255,255,0.45)",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              Contestant
            </Typography>
            <Typography
              noWrap
              sx={{
                color: lightSurface ? "#222222" : "rgba(255,255,255,0.88)",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {name}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

/**
 * Approved videos list — filters locally by event tab + category.
 */
export default function CompetitionApprovedVideos({
  videos = [],
  activeEventId,
  categoryFilter = "",
  votingEnabled = false,
  votePurchase,
  onBuyVotes,
  remainingVotes = 0,
  maxFreeVotesPerDay = 1,
  dailyVotesUsed = 0,
  onVoted,
  onVoteStateChange,
  onVideosChange,
  onCategoryClick,
  lightSurface = false,
  competitionBanner = "",
}) {
  const [activeVideo, setActiveVideo] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);

  const rows = useMemo(
    () => rankCompetitionVideos(videos, activeEventId, categoryFilter),
    [videos, activeEventId, categoryFilter]
  );

  const handleVoted = (videoId, nextCount, meta) => {
    onVideosChange?.((prev) =>
      prev.map((v) =>
        String(v.id) === String(videoId)
          ? { ...v, voteCount: nextCount, vote_count: nextCount }
          : v
      )
    );
    setActiveVideo((cur) =>
      cur && String(cur.id) === String(videoId)
        ? { ...cur, voteCount: nextCount, vote_count: nextCount }
        : cur
    );
    onVoteStateChange?.(meta);
    onVoted?.(videoId, nextCount, meta);
  };

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          border: lightSurface
            ? "1px dashed #cccccc"
            : "1px dashed rgba(255,255,255,0.14)",
          borderRadius: 3,
          px: { xs: 3, md: 5 },
          py: { xs: 6, md: 8 },
          mt: 1,
          mb: 4,
          minHeight: { xs: 220, md: 280 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: lightSurface ? "#666666" : "rgba(255,255,255,0.5)",
          bgcolor: lightSurface ? MISS_GENERATION_DETAIL_SURFACE : "rgba(255,255,255,0.03)",
        }}
      >
        <Typography sx={{ fontSize: { xs: 14, md: 15 }, lineHeight: 1.6 }}>
          {categoryFilter
            ? "No approved videos or images in this category for this event yet."
            : "No approved videos or images for this event yet."}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Stack spacing={1.8}>
        {rows.map((v) => (
          <VideoCard
            key={v.id}
            video={v}
            votingEnabled={votingEnabled}
            onVoted={handleVoted}
            onOpen={setActiveVideo}
            onProfileOpen={setProfilePreview}
            onCategoryClick={onCategoryClick}
            votePurchase={votePurchase}
            onBuyVotes={onBuyVotes}
            remainingVotes={remainingVotes}
            maxFreeVotesPerDay={maxFreeVotesPerDay}
            dailyVotesUsed={dailyVotesUsed}
            lightSurface={lightSurface}
            competitionBanner={competitionBanner}
          />
        ))}
      </Stack>

      <Typography
        sx={{
          textAlign: "center",
          color: lightSurface ? "#666666" : "rgba(255,255,255,0.4)",
          fontSize: 12,
          mt: 2,
          mb: 1,
        }}
      >
        Showing {rows.length} submission{rows.length === 1 ? "" : "s"}
      </Typography>

      <CompetitionVideoModal
        open={Boolean(activeVideo)}
        onClose={() => setActiveVideo(null)}
        video={activeVideo}
        votingEnabled={votingEnabled}
        onVoted={handleVoted}
        competitionBanner={competitionBanner}
      />

      <ContestantProfileModal
        open={Boolean(profilePreview)}
        onClose={() => setProfilePreview(null)}
        image={profilePreview?.image}
        name={profilePreview?.name}
        contestant={profilePreview?.contestant}
      />
    </>
  );
}
